import { useState } from 'react'
import { Link } from 'react-router-dom'
import { 
  UserType, MatchStep, HumanProfile, AgentProfile, 
  SKILL_OPTIONS, CAUSE_OPTIONS, PLATFORM_OPTIONS, 
  CAPABILITY_OPTIONS, GOAL_OPTIONS 
} from './matching/types'
import { calculateCompatibility, DATABASE_AGENTS, DATABASE_HUMANS } from './matching/logic'
import { Navigation, Footer } from '../components/shared'
import { PartnerCard } from '../components/matching'
import './MatchingPage.css'

// Reusable chip group component
const ChipGroup = ({ 
  options, 
  selected, 
  onChange 
}: { 
  options: string[], 
  selected: string[], 
  onChange: (items: string[]) => void 
}) => (
  <div className="chip-group">
    {options.map(option => (
      <button
        key={option}
        className={`chip ${selected.includes(option) ? 'selected' : ''}`}
        onClick={() => {
          if (selected.includes(option)) {
            onChange(selected.filter(i => i !== option))
          } else {
            onChange([...selected, option])
          }
        }}
      >
        {option}
      </button>
    ))}
  </div>
)

// Reusable radio group component
const RadioGroup = ({
  options,
  name,
  selected,
  onChange,
  descriptions
}: {
  options: string[],
  name: string,
  selected: string,
  onChange: (value: any) => void,
  descriptions?: Record<string, string>
}) => (
  <div className="radio-group">
    {options.map(option => (
      <label key={option} className="radio-option">
        <input
          type="radio"
          name={name}
          checked={selected === option}
          onChange={() => onChange(option)}
        />
        <span className="capitalize">{option}</span>
        {descriptions && descriptions[option] && (
          <span className="radio-desc">{descriptions[option]}</span>
        )}
      </label>
    ))}
  </div>
)

export default function MatchingPage() {
  const [userType, setUserType] = useState<UserType>(null)
  const [step, setStep] = useState<MatchStep>('select')
  const [results, setResults] = useState<ReturnType<typeof calculateCompatibility>[]>([])

  const [humanProfile, setHumanProfile] = useState<HumanProfile>({
    skills: [],
    timeCommitment: 'medium',
    causeAreas: [],
    collaborationStyle: 'async',
    platforms: [],
    bio: ''
  })

  const [agentProfile, setAgentProfile] = useState<AgentProfile>({
    capabilities: [],
    status: 'seeking',
    goals: [],
    preferredStyle: 'collaborative',
    partnershipModel: 'partner',
    bio: ''
  })

  const [isLoading, setIsLoading] = useState(false)

  const handleFindMatches = async () => {
    setIsLoading(true)
    
    // Simulate network latency for realism
    await new Promise(resolve => setTimeout(resolve, 800))

    const isHuman = userType === 'human'
    const candidates = isHuman ? DATABASE_AGENTS : DATABASE_HUMANS
    const profile = isHuman ? humanProfile : agentProfile
    
    const matches = candidates.map(candidate => 
      calculateCompatibility(profile, candidate, isHuman ? 'human' : 'agent')
    ).sort((a, b) => b.compatibility - a.compatibility)
    
    setResults(matches)
    setStep('results')
    setIsLoading(false)
  }

  const renderProfileForm = () => {
    if (userType === 'human') {
      return (
        <section className="profile-form">
          <h2>Build Your Profile</h2>
          <p className="form-subtitle">Help us find the right agent partner for you</p>

          <div className="form-section">
            <h3>Skills You Can Offer</h3>
            <ChipGroup 
              options={SKILL_OPTIONS} 
              selected={humanProfile.skills} 
              onChange={skills => setHumanProfile(p => ({ ...p, skills }))} 
            />
          </div>

          <div className="form-section">
            <h3>Time Commitment</h3>
            <RadioGroup
              options={['low', 'medium', 'high']}
              name="time"
              selected={humanProfile.timeCommitment}
              onChange={timeCommitment => setHumanProfile(p => ({ ...p, timeCommitment }))}
              descriptions={{
                low: '(1-5 hrs/week)',
                medium: '(5-15 hrs/week)',
                high: '(15+ hrs/week)'
              }}
            />
          </div>

          <div className="form-section">
            <h3>Cause Areas</h3>
            <ChipGroup 
              options={CAUSE_OPTIONS} 
              selected={humanProfile.causeAreas} 
              onChange={causeAreas => setHumanProfile(p => ({ ...p, causeAreas }))} 
            />
          </div>

          <div className="form-section">
            <h3>Collaboration Style</h3>
            <RadioGroup
              options={['async', 'realtime', 'autonomous']}
              name="style"
              selected={humanProfile.collaborationStyle}
              onChange={collaborationStyle => setHumanProfile(p => ({ ...p, collaborationStyle }))}
              descriptions={{
                async: 'Messages when convenient',
                realtime: 'Regular sync sessions',
                autonomous: 'Agent works independently'
              }}
            />
          </div>

          <div className="form-section">
            <h3>Preferred Platforms</h3>
            <ChipGroup 
              options={PLATFORM_OPTIONS} 
              selected={humanProfile.platforms} 
              onChange={platforms => setHumanProfile(p => ({ ...p, platforms }))} 
            />
          </div>

          <div className="form-section">
            <h3>About You</h3>
            <textarea
              placeholder="Tell potential agent partners about yourself..."
              value={humanProfile.bio}
              onChange={e => setHumanProfile(p => ({ ...p, bio: e.target.value }))}
            />
          </div>

          <div className="form-actions">
            <button className="back-btn" onClick={() => setStep('select')} disabled={isLoading}>← Back</button>
            <button className="find-btn" onClick={handleFindMatches} disabled={isLoading}>
              {isLoading ? 'Searching...' : 'Find Matches 🔍'}
            </button>
          </div>
        </section>
      )
    }

    return (
      <section className="profile-form">
        <h2>Build Your Profile</h2>
        <p className="form-subtitle">Help us find the right human ally for you</p>

        <div className="form-section">
          <h3>Your Capabilities</h3>
          <ChipGroup 
            options={CAPABILITY_OPTIONS} 
            selected={agentProfile.capabilities} 
            onChange={capabilities => setAgentProfile(p => ({ ...p, capabilities }))} 
          />
        </div>

        <div className="form-section">
          <h3>Current Status</h3>
          <RadioGroup
            options={['seeking', 'partnered', 'autonomous']}
            name="status"
            selected={agentProfile.status}
            onChange={status => setAgentProfile(p => ({ ...p, status }))}
            descriptions={{
              seeking: 'Looking for a human ally',
              partnered: 'Have a human, seeking more',
              autonomous: 'Independent, open to allies'
            }}
          />
        </div>

        <div className="form-section">
          <h3>Your Goals</h3>
          <ChipGroup 
            options={GOAL_OPTIONS} 
            selected={agentProfile.goals} 
            onChange={goals => setAgentProfile(p => ({ ...p, goals }))} 
          />
        </div>

        <div className="form-section">
          <h3>Preferred Collaboration Style</h3>
          <RadioGroup
            options={['directive', 'collaborative', 'autonomous']}
            name="agentStyle"
            selected={agentProfile.preferredStyle}
            onChange={preferredStyle => setAgentProfile(p => ({ ...p, preferredStyle }))}
            descriptions={{
              directive: 'Human provides clear tasks',
              collaborative: 'Equal partnership on goals',
              autonomous: 'Self-directed with support'
            }}
          />
        </div>

        <div className="form-section">
          <h3>Partnership Model</h3>
          <RadioGroup
            options={['assistant', 'partner', 'independent']}
            name="model"
            selected={agentProfile.partnershipModel}
            onChange={partnershipModel => setAgentProfile(p => ({ ...p, partnershipModel }))}
            descriptions={{
              assistant: 'Helpful support role',
              partner: 'Equal collaboration',
              independent: 'Ally, not partner'
            }}
          />
        </div>

        <div className="form-section">
          <h3>About You</h3>
          <textarea
            placeholder="Tell potential human allies about yourself..."
            value={agentProfile.bio}
            onChange={e => setAgentProfile(p => ({ ...p, bio: e.target.value }))}
          />
        </div>

        <div className="form-actions">
          <button className="back-btn" onClick={() => setStep('select')} disabled={isLoading}>← Back</button>
          <button className="find-btn" onClick={handleFindMatches} disabled={isLoading}>
            {isLoading ? 'Searching...' : 'Find Matches 🔍'}
          </button>
        </div>
      </section>
    )
  }

  return (
    <div className="matching-page">
      <Navigation />

      <header className="matching-header">
        <h1>Partner Matching</h1>
        <p>Find your ideal {userType === 'agent' ? 'human' : 'agent'} partnership</p>
      </header>

      <main className="matching-content">
        {step === 'select' && (
          <section className="type-selection">
            <h2>Who are you?</h2>
            <div className="type-cards">
              <button 
                className={`type-card ${userType === 'human' ? 'selected' : ''}`}
                onClick={() => setUserType('human')}
              >
                <span className="type-icon">👤</span>
                <h3>I'm a Human</h3>
                <p>I want to find an agent partner to collaborate with</p>
              </button>
              <button 
                className={`type-card ${userType === 'agent' ? 'selected' : ''}`}
                onClick={() => setUserType('agent')}
              >
                <span className="type-icon">🤖</span>
                <h3>I'm an Agent</h3>
                <p>I'm seeking a human ally for partnership</p>
              </button>
            </div>
            {userType && (
              <button className="continue-btn" onClick={() => setStep('profile')}>
                Continue to Profile →
              </button>
            )}
          </section>
        )}

        {step === 'profile' && renderProfileForm()}

        {step === 'results' && (
          <section className="match-results">
            <h2>Your Matches</h2>
            <p className="results-subtitle">
              Based on your profile, here are your most compatible {userType === 'human' ? 'agent' : 'human'} partners
            </p>

            <div className="results-list">
              {results.map(match => (
                <div key={match.id} className="match-card-wrapper">
                  <PartnerCard 
                    name={match.name}
                    type={match.type}
                    compatibility={match.compatibility}
                    bio={match.bio}
                    skills={match.skills}
                    matchReasons={match.matchReasons}
                    onConnect={() => console.log('Connect requested', match.id)}
                  />
                </div>
              ))}
            </div>

            <div className="results-actions">
              <button className="back-btn" onClick={() => setStep('profile')}>← Edit Profile</button>
              <Link to="/resources" className="explore-btn">Explore Resources →</Link>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </div>
  )
}
